import React from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Box,
  Chip,
  Skeleton,
} from '@mui/material';
import {
  Description as DocumentIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Article as WordIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface Document {
  id: string;
  title: string;
  category: string;
  uploadedBy: string;
  uploadedAt: string;
  fileType: 'pdf' | 'docx' | 'xlsx' | 'other';
  size: number;
}

interface RecentDocumentsWidgetProps {
  documents?: Document[];
  loading?: boolean;
  onDocumentClick?: (documentId: string) => void;
  onViewAll?: () => void;
}

const getFileIcon = (fileType: string) => {
  switch (fileType) {
    case 'pdf':
      return <PdfIcon />;
    case 'docx':
      return <WordIcon />;
    case 'xlsx':
      return <ExcelIcon />;
    default:
      return <DocumentIcon />;
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const RecentDocumentsWidget: React.FC<RecentDocumentsWidgetProps> = ({
  documents = [],
  loading = false,
  onDocumentClick,
  onViewAll,
}) => {
  const { t } = useTranslation();
  const renderSkeleton = () => (
    <>
      {[1, 2, 3, 4, 5].map((i) => (
        <ListItem key={i}>
          <ListItemAvatar>
            <Skeleton variant="circular" width={40} height={40} />
          </ListItemAvatar>
          <ListItemText
            primary={<Skeleton variant="text" width="60%" />}
            secondary={<Skeleton variant="text" width="40%" />}
          />
          <ListItemSecondaryAction>
            <Skeleton variant="circular" width={24} height={24} />
          </ListItemSecondaryAction>
        </ListItem>
      ))}
    </>
  );

  return (
    <Paper elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{t('dashboard.recentDocuments')}</Typography>
        {onViewAll && (
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={onViewAll}
          >
            {t('dashboard.viewAll')}
          </Typography>
        )}
      </Box>

      <List sx={{ flexGrow: 1, overflow: 'auto' }}>
        {loading ? (
          renderSkeleton()
        ) : documents.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <DocumentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {t('documents.noDocumentsFound')}
            </Typography>
          </Box>
        ) : (
          documents.map((doc) => (
            <ListItem
              key={doc.id}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemButton onClick={() => onDocumentClick?.(doc.id)}>
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'primary.light' }}>
                  {getFileIcon(doc.fileType)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        maxWidth: 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={doc.title}
                    >
                      {doc.title}
                    </Typography>
                    <Chip label={doc.category} size="small" variant="outlined" />
                  </Box>
                }
                secondary={
                  <Box component="span" sx={{ display: 'block' }}>
                    <Typography variant="caption" color="text.secondary">
                      {doc.uploadedBy} • {formatDistanceToNow(new Date(doc.uploadedAt), { addSuffix: true })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      • {formatFileSize(doc.size)}
                    </Typography>
                  </Box>
                }
                primaryTypographyProps={{ component: 'div' }}
                secondaryTypographyProps={{ component: 'div' }}
              />
              </ListItemButton>
              <ListItemSecondaryAction>
                <IconButton edge="end" size="small">
                  <MoreIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))
        )}
      </List>
    </Paper>
  );
};

export default RecentDocumentsWidget;
